/**
 * Native replacements for Lodash function utilities
 */

type DebounceOptions = {
  leading?: boolean;
  trailing?: boolean;
};

/**
 * Creates a debounced function that delays invoking func until after wait milliseconds
 * have elapsed since the last time the debounced function was invoked.
 * Supports leading/trailing edge options matching lodash behavior.
 */
// biome-ignore lint/suspicious/noExplicitAny: Need to accept any function signature for debounce
export function debounce<F extends (...args: any[]) => any>(
  func: F,
  wait: number,
  { leading = false, trailing = true }: DebounceOptions = {},
): ((...args: Parameters<F>) => void) & { cancel: () => void } {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;

  const debounced = function (this: ThisParameterType<F>, ...args: Parameters<F>): void {
    const invokeLeading = leading && !timeoutId;

    clearTimeout(timeoutId);

    timeoutId = setTimeout(() => {
      timeoutId = undefined;
      if (trailing && !invokeLeading) {
        func.apply(this, args);
      }
    }, wait);

    if (invokeLeading) {
      func.apply(this, args);
    }
  };

  debounced.cancel = () => {
    clearTimeout(timeoutId);
    timeoutId = undefined;
  };

  return debounced as typeof debounced & { cancel: () => void };
}

/**
 * Creates a throttled function that only invokes func at most once per every wait milliseconds
 */
// biome-ignore lint/suspicious/noExplicitAny: Need to accept any function signature for throttle
export function throttle<F extends (...args: any[]) => any>(
  func: F,
  wait: number,
): ((...args: Parameters<F>) => void) & { cancel: () => void } {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  let lastRan: number | undefined;

  const throttled = function (this: ThisParameterType<F>, ...args: Parameters<F>): void {
    if (lastRan === undefined) {
      func.apply(this, args);
      lastRan = Date.now();
    } else {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(
        () => {
          if (Date.now() - (lastRan ?? 0) >= wait) {
            func.apply(this, args);
            lastRan = Date.now();
          }
        },
        wait - (Date.now() - lastRan),
      );
    }
  };

  throttled.cancel = () => {
    clearTimeout(timeoutId);
    lastRan = undefined;
  };

  return throttled as typeof throttled & { cancel: () => void };
}

/**
 * Invokes a function n times, returning an array of the results
 * @example times(3, i => i * 2) // [0, 2, 4]
 */
export function times<T>(n: number, iteratee: (index: number) => T): T[] {
  return Array.from({ length: n }, (_, i) => iteratee(i));
}
